/**
 * Chart labels, one set of rules (mobile pass MX-S): an axis or end label never clips at either edge of its svg (it is inset by its measured width), no label is under the
 * 12 px floor, and ticks that would touch are thinned by the width they have. Pure: nothing here reads the DOM.
 */
export type LabelAnchor = "start" | "middle" | "end";

/** The smallest label on screen, in CSS px. */
export const LABEL_FLOOR = 12;
/** A label's font size: never under the floor, at any chart width (the owner's bar: no player-facing text under 12 px). */
export const labelSize = (size: number, _chartWidth: number): number => Math.max(LABEL_FLOOR, size);

/** The width a label takes at `size` px (an average glyph is 0.56 em, bold 0.6): an estimate, so the same answer renders in a test and in a browser. */
export const labelWidth = (text: string, size: number, weight = 400): number => text.length * size * (weight >= 600 ? 0.6 : 0.56);

/** The x (and anchor) that keeps a label of this text inside [pad, chartWidth - pad]: it slides along the edge it would cross, keeping its anchor. */
export function insetLabel(x: number, text: string, size: number, chartWidth: number, anchor: LabelAnchor = "middle", weight = 400, pad = 2): { readonly x: number; readonly anchor: LabelAnchor } {
  const w = labelWidth(text, size, weight);
  const left0 = anchor === "start" ? x : anchor === "end" ? x - w : x - w / 2;
  const left = Math.max(pad, Math.min(left0, chartWidth - pad - w));
  return { x: anchor === "start" ? left : anchor === "end" ? left + w : left + w / 2, anchor };
}

/** Of labels along an axis (sorted by `at`, centred), the indexes to draw: the first, then each that clears the one before by `gap`; the last always stays (the one before it gives way). */
export function thinLabels(labels: readonly { readonly at: number; readonly text: string }[], size: number, gap = 8): ReadonlySet<number> {
  const keep: number[] = [];
  const half = (i: number): number => labelWidth(labels[i]?.text ?? "", size) / 2;
  const clears = (a: number, b: number): boolean => (labels[b]?.at ?? 0) - half(b) >= (labels[a]?.at ?? 0) + half(a) + gap;
  labels.forEach((_, i) => { const prev = keep.at(-1); if (prev === undefined || clears(prev, i)) keep.push(i); });
  const lastIdx = labels.length - 1;
  if (lastIdx >= 0 && keep.at(-1) !== lastIdx) {
    while (keep.length > 1 && !clears(keep.at(-1) as number, lastIdx)) keep.pop();
    if (keep.length === 1 && !clears(keep[0] as number, lastIdx)) keep.pop();
    keep.push(lastIdx);
  }
  return new Set(keep);
}
